/**
 * Persona Kütüphanesi — kalıcı kimliklerin ve takımların ana ekranı.
 *
 * `AppShell` altında tam sayfa; `AdminPage`/`DashboardPage` ile aynı
 * ham-Tailwind dilini kullanır (bkz. `components/library/ui.tsx` başlığı).
 *
 * Sayfa iki sekmedir çünkü kullanıcının iki farklı işi var: tek tek kimlik
 * yönetmek ve o kimlikleri kadrolara toplamak. Aynı listede karıştırmak,
 * "hangisi tartışmaya girecek?" sorusunu belirsizleştiriyordu.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { PersonaComposer, roleLabel } from "../components/library/PersonaComposer";
import { PersonaDetail } from "../components/library/PersonaDetail";
import { TeamEditor } from "../components/library/TeamEditor";
import { UnitTree, type UnitSelection } from "../components/library/UnitTree";
import { UnitDocumentsModal } from "../components/library/UnitDocumentsModal";
import {
  Btn,
  Chip,
  EmptyState,
  ErrorBanner,
  PersonaAvatar,
  TextInput,
} from "../components/library/ui";
import {
  approvalRate,
  unitAncestors,
  unitPathLabel,
  UNIT_KIND_LABELS,
  type LibraryPersonaBrief,
  type PersonaTeam,
  type PersonaUnit,
} from "../domain/personaLibrary";
import { usePersonaLibraryStore } from "../store/personaLibraryStore";
import { useDebateStore } from "../store/debateStore";
import { deleteTeam, LibraryApiError } from "../transport/personaLibraryClient";

type Tab = "personas" | "teams";

/**
 * Personanın organizasyondaki YERİ — yalnız yaprak birim adı yetmiyor.
 *
 * "Silah Sistemleri Müdürlüğü" tek başına hangi kuruma bağlı olduğunu
 * söylemiyor; üstelik dar kartta kırpılınca "Silah Siste…" kalıyordu.
 * Tam zinciri gösteriyoruz: derinleştikçe baştan kısaltıp son iki seviyeyi
 * her zaman okunur tutuyoruz, tam yol `title` ile erişilebilir kalıyor.
 */
function UnitBreadcrumb({ chain }: { chain: string[] }) {
  if (chain.length === 0) return null;
  const full = chain.join(" › ");
  const shown = chain.length > 3 ? ["…", ...chain.slice(-2)] : chain;
  return (
    <div
      className="mt-1 flex items-start gap-1 text-[10.5px] leading-snug text-slate-500"
      title={full}
    >
      <span className="shrink-0" aria-hidden>
        📁
      </span>
      <span className="line-clamp-2 break-words">
        {shown.map((name, i) => (
          <span key={`${name}-${i}`}>
            {i > 0 && <span className="text-slate-700"> › </span>}
            <span className={i === shown.length - 1 ? "text-slate-400" : undefined}>
              {name}
            </span>
          </span>
        ))}
      </span>
    </div>
  );
}

function PersonaCard({
  persona,
  unitChain,
  onOpen,
}: {
  persona: LibraryPersonaBrief;
  /** Kökten yaprağa birim adları; boş ⇒ atanmamış. */
  unitChain: string[];
  onOpen: () => void;
}) {
  const rate = approvalRate(persona.stats);
  const strengths = persona.selfModel.strengths ?? [];

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full flex-col gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 text-left transition-all duration-200 hover:-translate-y-px hover:border-violet-500/40 hover:bg-white/[0.04]"
    >
      <div className="flex items-start gap-3">
        <PersonaAvatar
          url={persona.avatarUrl}
          name={persona.displayName}
          color={persona.themeColor}
          size={44}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-semibold text-white">
            {persona.displayName}
          </div>
          <div
            className="line-clamp-2 text-[11.5px] leading-snug text-slate-400"
            title={persona.expertise ?? roleLabel(persona.role)}
          >
            {persona.expertise ?? roleLabel(persona.role)}
          </div>
          {unitChain.length > 0 ? (
            <UnitBreadcrumb chain={unitChain} />
          ) : (
            <div className="mt-1 text-[10.5px] text-slate-600">○ Atanmamış</div>
          )}
        </div>
        {persona.isArchived && <Chip tone="rose">arşiv</Chip>}
      </div>

      {persona.summary && (
        <p className="line-clamp-2 text-[11.5px] leading-relaxed text-slate-500">
          {persona.summary}
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        <Chip tone="violet">{roleLabel(persona.role)}</Chip>
        <Chip title="Nötrden sapmış en güçlü mizaç eksenleri">{persona.temperament}</Chip>
        {persona.biography && (
          <Chip tone="cyan" title="Yaş · deneyim · kıdem · sektör">
            {persona.biography}
          </Chip>
        )}
      </div>

      {strengths.length > 0 && (
        <div className="text-[11px] leading-relaxed text-emerald-300/70">
          ✓ {strengths.slice(0, 2).join(" · ")}
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/[0.05] pt-2 text-[10.5px] text-slate-500">
        <span title="Katıldığı tartışma sayısı">{persona.stats.debateCount} tartışma</span>
        <span title="Kalıcı hafızasındaki not sayısı">{persona.stats.memoryCount} hafıza</span>
        {rate !== null && (
          <span className={rate >= 0.6 ? "text-emerald-400/80" : "text-amber-400/80"}>
            %{Math.round(rate * 100)} beğeni
          </span>
        )}
      </div>
    </button>
  );
}

function TeamCard({
  team,
  unitNameFor,
  onEdit,
  onDelete,
  onUse,
}: {
  team: PersonaTeam;
  unitNameFor: (unitId: string | null) => string;
  onEdit: () => void;
  onDelete: () => void;
  onUse: () => void;
}) {
  // Takımın asıl karakteri kaç BİRİMİ kestiği. Topoloji rozeti buradan
  // kaldırıldı: o, kadronun kimliği değil tartışmanın ayarı.
  const representedUnits = Array.from(
    new Set(team.members.map((m) => m.unitId ?? null)),
  );

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
      <div className="flex items-start gap-3">
        <span
          className="mt-1 h-8 w-1 shrink-0 rounded-full"
          style={{ background: team.accentColor }}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-white">{team.name}</div>
          {team.description && (
            <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-relaxed text-slate-500">
              {team.description}
            </p>
          )}
        </div>
        {representedUnits.length > 1 && (
          <Chip tone="cyan" title={representedUnits.map(unitNameFor).join(" · ")}>
            {representedUnits.length} birim
          </Chip>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {team.members.length === 0 ? (
          <span className="text-[11.5px] text-amber-400">Kadro boş</span>
        ) : (
          team.members.map((m) => (
            <span
              key={m.id}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] py-0.5 pl-0.5 pr-2"
              title={`${unitNameFor(m.unitId)} · ${m.expertise ?? roleLabel(m.role)}`}
            >
              <PersonaAvatar
                url={m.avatarUrl}
                name={m.displayName}
                color={m.themeColor}
                size={20}
              />
              <span className="text-[11px] text-slate-300">{m.displayName}</span>
              <span className="text-[9.5px] text-slate-600">{unitNameFor(m.unitId)}</span>
            </span>
          ))
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-white/[0.05] pt-2">
        <Btn
          size="sm"
          tone="primary"
          onClick={onUse}
          disabled={team.members.length === 0}
          title="Bu kadroyla yeni bir tartışma başlat"
        >
          Tartışma Başlat
        </Btn>
        <Btn size="sm" tone="ghost" onClick={onEdit}>
          Düzenle
        </Btn>
        <Btn size="sm" tone="ghost" className="hover:text-rose-400" onClick={onDelete}>
          Sil
        </Btn>
        <span className="ml-auto text-[10.5px] text-slate-600">
          {team.debateCount} tartışma
        </span>
      </div>
    </div>
  );
}

export function PersonaLibraryPage() {
  const navigate = useNavigate();
  const {
    catalog,
    personas,
    teams,
    units,
    unassignedCount,
    loading,
    unavailable,
    error,
    load,
    refreshPersonas,
    refreshTeams,
    refreshUnits,
    clearError,
  } = usePersonaLibraryStore();

  const setLibraryTeam = useDebateStore((s) => s.setLibraryTeam);
  const setLibraryUnit = useDebateStore((s) => s.setLibraryUnit);
  const openWizard = useDebateStore((s) => s.openWizard);

  const [tab, setTab] = useState<Tab>("personas");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [composing, setComposing] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [teamEditing, setTeamEditing] = useState<PersonaTeam | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selection, setSelection] = useState<UnitSelection>({ kind: "all" });
  const [unitDocsFor, setUnitDocsFor] = useState<PersonaUnit | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);

  const unitNameFor = useMemo(
    () => (unitId: string | null) =>
      unitId ? (unitById.get(unitId)?.name ?? "Bilinmeyen birim") : "Atanmamış",
    [unitById],
  );

  /** Seçili düğüm + tüm torunları — filtre alt ağacı kapsar. */
  const selectedSubtree = useMemo(() => {
    if (selection.kind !== "unit") return null;
    const node = unitById.get(selection.id);
    if (!node) return null;
    const ids = new Set<string>([node.id]);
    for (const u of units) {
      if ((u.path || "").startsWith(`${node.path}/`)) ids.add(u.id);
    }
    return ids;
  }, [selection, units, unitById]);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("tr-TR");
    return personas.filter((p) => {
      if (!showArchived && p.isArchived) return false;
      if (selection.kind === "unassigned" && p.unitId) return false;
      if (selectedSubtree && !(p.unitId && selectedSubtree.has(p.unitId))) return false;
      if (!needle) return true;
      return (
        p.title.toLocaleLowerCase("tr-TR").includes(needle) ||
        p.displayName.toLocaleLowerCase("tr-TR").includes(needle) ||
        (p.expertise ?? "").toLocaleLowerCase("tr-TR").includes(needle) ||
        p.tags.some((t) => t.toLocaleLowerCase("tr-TR").includes(needle))
      );
    });
  }, [personas, search, showArchived, selection, selectedSubtree]);

  function startWithTeam(team: PersonaTeam) {
    // Yalnız kadro seçilir. Topoloji ARTIK takımdan gelmiyor: bir görev
    // gücü farklı soru tipleri için yeniden kullanılır ve doğru tartışma
    // düzeni soruya göre değişir — kadroya sabitlemek yanlış varsayımdı.
    setLibraryTeam(team.id);
    openWizard();
    navigate("/debate");
  }

  function startWithUnit(unitId: string) {
    setLibraryUnit(unitId);
    openWizard();
    navigate("/debate");
  }

  const selectedUnit = selection.kind === "unit" ? unitById.get(selection.id) : undefined;

  if (unavailable) {
    return (
      <AppShell>
        <main className="mx-auto max-w-4xl px-6 py-16">
          <EmptyState
            title="Persona Kütüphanesi bu sunucuda kapalı"
            description="Yönetici MACDM_FEATURE_PERSONA_LIBRARY ayarını etkinleştirdiğinde kalıcı personalar, takımlar ve hafıza yönetimi burada görünecek."
          />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="mx-auto max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="bg-gradient-to-r from-white to-violet-200 bg-clip-text text-[22px] font-semibold tracking-tight text-transparent">
              Persona Kütüphanesi
            </h1>
            <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-slate-500">
              Kalıcı uzman kimlikleri tasarla. Her persona katıldığı tartışmaları hatırlar,
              kendi belge arşivinden alıntı yapar ve verdiğin geri bildirimle davranışını
              düzeltir.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {tab === "personas" ? (
              <Btn tone="primary" onClick={() => setComposing(true)}>
                + Yeni Persona
              </Btn>
            ) : (
              <Btn
                tone="primary"
                onClick={() => {
                  setTeamEditing(null);
                  setTeamModalOpen(true);
                }}
                disabled={personas.length === 0}
              >
                + Yeni Takım
              </Btn>
            )}
          </div>
        </header>

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-xl border border-white/[0.06] bg-white/[0.03] p-1">
            <button
              type="button"
              onClick={() => setTab("personas")}
              className={`rounded-lg px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${
                tab === "personas"
                  ? "bg-white/[0.09] text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Personalar ({personas.filter((p) => !p.isArchived).length})
            </button>
            <button
              type="button"
              onClick={() => setTab("teams")}
              className={`rounded-lg px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${
                tab === "teams" ? "bg-white/[0.09] text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Takımlar ({teams.length})
            </button>
          </div>

          {tab === "personas" && (
            <>
              <div className="w-64">
                <TextInput
                  value={search}
                  placeholder="Ara: ad, unvan, etiket…"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-slate-400">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="accent-violet-500"
                />
                Arşivi göster
              </label>
            </>
          )}
        </div>

        {(error || localError) && (
          <div className="mb-4">
            <ErrorBanner
              message={error ?? localError ?? ""}
              onDismiss={() => {
                clearError();
                setLocalError(null);
              }}
            />
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-[13px] text-slate-500">Yükleniyor…</div>
        ) : tab === "personas" ? (
          <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
            <UnitTree
              units={units}
              unassignedCount={unassignedCount}
              totalCount={personas.filter((p) => !p.isArchived).length}
              selection={selection}
              onSelect={setSelection}
              onChanged={() => {
                void refreshUnits();
                void refreshPersonas();
              }}
            />

            <div className="min-w-0 space-y-3">
              {selectedUnit && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/[0.07] bg-white/[0.02] px-3.5 py-2.5">
                  <div className="min-w-0 flex-1">
                    {selectedUnit.depth > 0 && (
                      <div className="mb-0.5 truncate text-[10.5px] text-slate-600">
                        {unitAncestors(units, selectedUnit.id)
                          .slice(0, -1)
                          .map((u) => u.name)
                          .join(" › ")}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[13px] font-semibold text-white">
                        {selectedUnit.name}
                      </span>
                      <Chip>{UNIT_KIND_LABELS[selectedUnit.kind]}</Chip>
                    </div>
                    <p className="mt-0.5 text-[11.5px] leading-snug text-slate-500">
                      {selectedUnit.mission ||
                        selectedUnit.description ||
                        "Alt birimler dahil tüm personalar listeleniyor."}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Btn
                      size="sm"
                      tone="outline"
                      onClick={() => setUnitDocsFor(selectedUnit)}
                      title="Bu birimin ve alt birimlerinin tüm personalarına açık ortak belgeler"
                    >
                      📁 Birim Belgeleri
                    </Btn>
                    <Btn
                      size="sm"
                      tone="primary"
                      disabled={visible.length === 0}
                      onClick={() => startWithUnit(selectedUnit.id)}
                      title="Bu birimdeki tüm personalarla (alt birimler dahil) tartışma başlat"
                    >
                      Tartışma Başlat ({selectedUnit.totalCount})
                    </Btn>
                  </div>
                </div>
              )}

              {visible.length === 0 ? (
                <EmptyState
                  title={
                    search
                      ? "Eşleşen persona yok"
                      : selection.kind === "unit"
                        ? "Bu birimde persona yok"
                        : selection.kind === "unassigned"
                          ? "Atanmamış persona yok"
                          : "Kütüphane boş"
                  }
                  description={
                    search
                      ? "Arama terimini değiştirmeyi dene."
                      : selection.kind === "unit"
                        ? "Bir personayı bu birime taşımak için kartını aç ve Kimlik sekmesindeki 'Birim' alanını kullan."
                        : "Bir unvan yazarak başla — model senin için tam bir kimlik tasarlasın; sonra mizacını ayarla, belgelerini yükle ve tartışmalarda kullan."
                  }
                  action={
                    !search &&
                    selection.kind === "all" && (
                      <Btn tone="primary" onClick={() => setComposing(true)}>
                        İlk Personayı Oluştur
                      </Btn>
                    )
                  }
                />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {visible.map((p) => (
                    <PersonaCard
                      key={p.id}
                      persona={p}
                      unitChain={
                        p.unitId ? unitAncestors(units, p.unitId).map((u) => u.name) : []
                      }
                      onOpen={() => setDetailId(p.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : teams.length === 0 ? (
          <EmptyState
            title="Henüz takım yok"
            description="Birlikte çalışmasını istediğin personaları bir kadroya topla; tartışma başlatırken tek tıkla o konseyi masaya oturt."
            action={
              personas.length > 0 && (
                <Btn
                  tone="primary"
                  onClick={() => {
                    setTeamEditing(null);
                    setTeamModalOpen(true);
                  }}
                >
                  İlk Takımı Kur
                </Btn>
              )
            }
          />
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {teams.map((t) => (
              <TeamCard
                key={t.id}
                team={t}
                unitNameFor={unitNameFor}
                onUse={() => startWithTeam(t)}
                onEdit={() => {
                  setTeamEditing(t);
                  setTeamModalOpen(true);
                }}
                onDelete={async () => {
                  if (!confirm(`"${t.name}" takımı silinsin mi? Personalar silinmez.`)) return;
                  try {
                    await deleteTeam(t.id);
                    await refreshTeams();
                  } catch (err) {
                    setLocalError(
                      err instanceof LibraryApiError ? err.message : "Takım silinemedi.",
                    );
                  }
                }}
              />
            ))}
          </div>
        )}
      </main>

      <PersonaComposer
        open={composing}
        catalog={catalog}
        onClose={() => setComposing(false)}
        onCreated={(created) => {
          void refreshPersonas();
          setDetailId(created.id);
        }}
      />

      {detailId && (
        <PersonaDetail
          personaId={detailId}
          catalog={catalog}
          units={units}
          onClose={() => setDetailId(null)}
          onMutated={() => {
            void refreshPersonas();
            void refreshTeams();
            void refreshUnits();
          }}
        />
      )}

      <TeamEditor
        open={teamModalOpen}
        team={teamEditing}
        personas={personas}
        units={units}
        onClose={() => setTeamModalOpen(false)}
        onSaved={() => void refreshTeams()}
      />

      {unitDocsFor && (
        <UnitDocumentsModal
          unit={unitDocsFor}
          unitPath={unitPathLabel(units, unitDocsFor.id)}
          onClose={() => setUnitDocsFor(null)}
          onChanged={() => void refreshUnits()}
        />
      )}
    </AppShell>
  );
}

export default PersonaLibraryPage;
